// ─── src/chat/RecommendationCard.jsx ─────────────────────────────────────────
import { useState, useEffect, useRef } from "react";
import Image from "../components/ui/Image";
import { useShortlist } from "../shortlist/ShortlistContext";
import { useAura } from "../stores/auraCore";
import Icon, { StarRating } from "./Icons";
import CuratedIndexBadge from "../components/ui/CuratedIndexBadge";
import { trackAuraClick } from "../services/auraClient";
import { cleanDashes } from "../utils/cleanText";
import { listingProfileUrl } from "../utils/listingUrl";

const GOLD = "#d39a21";

// ── Capability chip labels ────────────────────────────────────────────────────
// Aura's match_reasons arrive as couple-readable prose fragments. The full
// sentence stays in the reason line; here we compress each into a tight,
// scannable chip label. Dynamic phrases (bedrooms, cuisine, curfew, airport)
// are pattern-matched; the rest map 1:1.
const CAP_LABELS = {
  "exclusive use of the venue":        "Exclusive Use",
  "licensed for civil ceremonies":     "Civil Ceremony",
  "external caterers welcome":         "External Catering",
  "halal catering":                    "Halal Catering",
  "kosher catering":                   "Kosher Catering",
  "corkage and bring-your-own wine":   "Corkage",
  "live bands welcome":                "Live Bands",
  "djs welcome":                       "DJ Sets",
  "outdoor music permitted":           "Outdoor Music",
  "dedicated after-party space":       "After Party",
  "fireworks permitted":               "Fireworks",
  "drone shows permitted":             "Drone Shows",
  "in-house wedding planning team":    "In-House Planner",
  "on-site wedding coordinator":       "On-Site Coordinator",
  "welcomes your own planner":         "Bring Your Planner",
  "private-jet airport nearby":        "Private Jet",
  "helicopter transfers":              "Helicopter Access",
  "boat access":                       "Boat Access",
  "guest shuttle service":             "Shuttle Service",
  "on-site parking":                   "On-Site Parking",
  "welcome events":                    "Welcome Event",
  "pool parties":                      "Pool Party",
  "beach parties":                     "Beach Party",
  "excursion days":                    "Excursions",
  "dedicated wedding-breakfast space": "Wedding Breakfast",
  "farewell brunch":                   "Farewell Brunch",
  "weekend weddings":                  "Weekend Wedding",
  "three-day weddings":                "Three-Day Wedding",
  "multi-day weddings":                "Multi-Day Wedding",
  "full-buyout weddings":              "Full Buyout",
};

function capabilityChipLabel(phrase) {
  if (!phrase) return null;
  const p = String(phrase).toLowerCase().trim();
  if (p.startsWith("on-site accommodation")) return "On Site Bedrooms";
  const music = p.match(/^music until (.+)$/);
  if (music) return `Music Until ${music[1]}`;
  const cuisine = p.match(/^(\w+) cuisine via your own caterer$/);
  if (cuisine) return `${cuisine[1].charAt(0).toUpperCase()}${cuisine[1].slice(1)} Cuisine`;
  const airport = p.match(/^(\d+) minutes from /);
  if (airport) return `Airport ${airport[1]}min`;
  if (CAP_LABELS[p]) return CAP_LABELS[p];
  // Graceful fallback, Title-case and drop filler verbs
  return p.replace(/\b(welcome|permitted|available)\b/g, "").replace(/\s+/g, " ").trim()
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function deriveCapabilityChips(matchReasons) {
  if (!Array.isArray(matchReasons) || matchReasons.length === 0) return [];
  const seen = new Set();
  const out = [];
  for (const r of matchReasons) {
    const label = capabilityChipLabel(r);
    if (label && !seen.has(label)) { seen.add(label); out.push(label); }
  }
  return out;
}

function getT(dark) {
  if (dark) return {
    cardBg:      "rgba(255,255,255,0.02)",
    cardHovBg:   "rgba(255,255,255,0.04)",
    border:      "rgba(255,255,255,0.07)",
    borderHov:   "rgba(211,154,33,0.28)",
    nameColor:   "#f5f0e8",
    locColor:    "rgba(245,240,232,0.38)",
    ratingColor: "rgba(245,240,232,0.45)",
    capColor:    "rgba(245,240,232,0.32)",
    divider:     "rgba(255,255,255,0.06)",
    qvColor:     "rgba(245,240,232,0.45)",
    qvBg:        "rgba(255,255,255,0.04)",
    qvBorder:    "rgba(255,255,255,0.1)",
    tagColor:    "rgba(211,154,33,0.65)",
    tagBorder:   "rgba(211,154,33,0.18)",
  };
  return {
    cardBg:      "#FFFFFF",
    cardHovBg:   "#FDFCFA",
    border:      "rgba(26,23,20,0.1)",
    borderHov:   "rgba(211,154,33,0.4)",
    nameColor:   "#1A1714",
    locColor:    "rgba(26,23,20,0.45)",
    ratingColor: "rgba(26,23,20,0.5)",
    capColor:    "rgba(26,23,20,0.4)",
    divider:     "rgba(26,23,20,0.08)",
    qvColor:     "rgba(26,23,20,0.5)",
    qvBg:        "rgba(26,23,20,0.04)",
    qvBorder:    "rgba(26,23,20,0.12)",
    tagColor:    "#8B7432",
    tagBorder:   "rgba(211,154,33,0.3)",
  };
}

export default function RecommendationCard({ item, darkMode = true, onQuickView, onViewFull, onEnquire, queryId, sessionId, isActive = false, isAuraShortlisted = false, isEnquired = false }) {
  const { isShortlisted, toggleItem } = useShortlist();
  const setActiveVenueId   = useAura((s) => s.setActiveVenueId);
  const auraShortlistVenue  = useAura((s) => s.auraShortlistVenue);
  const auraShortlistRemove = useAura((s) => s.auraShortlistRemove);
  const sendMessage         = useAura((s) => s.sendMessage);
  const auraShortlistedIds  = useAura((s) => s.auraShortlistedIds);

  const [hov, setHov] = useState(false);
  const [currentImgIndex, setCurrentImgIndex] = useState(0);
  const timerRef = useRef(null);

  const saved          = isShortlisted(item.id);
  const shortlistFull  = auraShortlistedIds.length >= 3 && !isAuraShortlisted;
  const isVendor       = item.type === "vendor";
  const features       = item.specialties || item.styles || [];
  const T              = getT(darkMode);

  // Build combined media array: images + video (same source logic as LuxuryVenueCard)
  const rawImgs = item.imgs || (item.hero_images ? item.hero_images : []);
  const imageMedia = (rawImgs.length > 0 ? rawImgs : ["/placeholder-venue.jpg"]).map(img => ({
    type: "image",
    src: typeof img === "string" ? img : (img?.src || img?.url || ""),
  })).filter(m => m.src);

  const videoMedia = item.videoUrl ? [{ type: "video", src: item.videoUrl }] : [];
  const allMedia   = [...imageMedia, ...videoMedia];
  const totalMedia = allMedia.length;
  const currentMedia  = allMedia[currentImgIndex] ?? imageMedia[0] ?? { type: "image", src: "/placeholder-venue.jpg" };
  const isVideoSlide  = currentMedia.type === "video";
  const listingUrl    = listingProfileUrl(item);

  // Parse YouTube / Vimeo for video slides
  const ytMatch  = isVideoSlide ? currentMedia.src?.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([A-Za-z0-9_-]{11})/) : null;
  const vmMatch  = isVideoSlide ? currentMedia.src?.match(/(?:vimeo\.com\/|player\.vimeo\.com\/video\/)(\d+)/) : null;
  const ytId     = ytMatch?.[1] ?? null;
  const vimeoId  = vmMatch?.[1] ?? null;

  // Autoplay images when In Focus; pause on video slide
  useEffect(() => {
    clearInterval(timerRef.current);
    if (isActive && !isVideoSlide && imageMedia.length > 1) {
      timerRef.current = setInterval(() => {
        setCurrentImgIndex(i => (i + 1) % imageMedia.length);
      }, 3000);
    }
    return () => clearInterval(timerRef.current);
  }, [isActive, isVideoSlide, imageMedia.length]);

  const nextImage = (e) => {
    e.stopPropagation();
    e.preventDefault();
    setCurrentImgIndex((prev) => (prev + 1) % totalMedia);
  };

  const prevImage = (e) => {
    e.stopPropagation();
    e.preventDefault();
    setCurrentImgIndex((prev) => (prev - 1 + totalMedia) % totalMedia);
  };

  const handleQuickView = (e) => {
    e.stopPropagation();
    e.preventDefault();
    if (queryId && sessionId) trackAuraClick(queryId, sessionId, item.id, item.rank ?? 0, 'card');
    onQuickView?.(item);
  };

  return (
    <div
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        borderRadius: "var(--lwd-radius-card)",
        overflow:     "hidden",
        background:    hov ? T.cardHovBg : T.cardBg,
        border:        `1px solid ${isActive ? "rgba(211,154,33,0.6)" : isAuraShortlisted ? "rgba(211,154,33,0.35)" : hov ? T.borderHov : T.border}`,
        boxShadow:     isActive
          ? `0 0 3px rgba(211,154,33,0.28)`
          : hov ? "0 4px 20px rgba(0,0,0,0.08)" : "0 1px 6px rgba(0,0,0,0.04)",
        transition:   "all 0.3s ease",
        marginBottom: 14,
        position:     "relative",
      }}
    >
      {/* ── Image, click to focus this venue in Aura ── */}
      <div
        role="button"
        tabIndex={0}
        aria-label={`Ask Aura about ${item.name}`}
        onClick={() => {
          setActiveVenueId(item.id);
          auraShortlistVenue(item);
          sendMessage(`Tell me more about ${item.name}`);
          if (queryId && sessionId) trackAuraClick(queryId, sessionId, item.id, item.rank ?? 0, 'card');
        }}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") e.currentTarget.click(); }}
        style={{
          display:  "block",
          height:   152,
          position: "relative",
          overflow: "hidden",
          background: "#0a0806",
          cursor:   isActive ? "default" : "pointer",
        }}
      >
        {/* Image layers, all stacked, crossfade via opacity */}
        {imageMedia.map((img, i) => (
          <Image
            key={img.src}
            width={600}
            src={img.src}
            alt={i === 0 ? item.name : ""}
            loading={i === 0 ? "eager" : "lazy"}
            style={{
              position:   "absolute",
              inset:      0,
              width:      "100%",
              height:     "100%",
              objectFit:  "cover",
              opacity:    (!isVideoSlide && currentImgIndex === i) ? 1 : 0,
              transform:  hov ? "scale(1.03)" : "scale(1)",
              transition: "opacity 0.65s ease, transform 0.55s ease",
              willChange: "opacity",
            }}
          />
        ))}

        {/* Video slide: YouTube / Vimeo iframe or native video */}
        {isVideoSlide && (
          <div style={{ position: "absolute", inset: 0, background: "#000" }}>
            {/* Hero image behind iframe for clean load */}
            {imageMedia[0]?.src && (
              <Image src={imageMedia[0].src} width={600} alt="" style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", opacity: 0.4 }} />
            )}
            {ytId ? (
              <iframe
                key={ytId}
                src={`https://www.youtube.com/embed/${ytId}?autoplay=1&mute=1&loop=1&playlist=${ytId}&controls=0&rel=0&modestbranding=1&playsinline=1`}
                allow="autoplay; encrypted-media"
                title={item.name}
                style={{ position: "absolute", inset: 0, width: "100%", height: "100%", border: "none", pointerEvents: "none" }}
              />
            ) : vimeoId ? (
              <iframe
                key={vimeoId}
                src={`https://player.vimeo.com/video/${vimeoId}?background=1&autoplay=1&loop=1&muted=1`}
                allow="autoplay; fullscreen"
                title={item.name}
                style={{ position: "absolute", inset: 0, width: "100%", height: "100%", border: "none", pointerEvents: "none" }}
              />
            ) : (
              <video
                key={currentMedia.src}
                src={currentMedia.src}
                autoPlay muted loop playsInline
                style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }}
              />
            )}
            {/* Video label */}
            <div style={{ position: "absolute", top: 6, right: 6, zIndex: 3, padding: "2px 7px", borderRadius: 10, background: "rgba(0,0,0,0.55)", border: "1px solid rgba(211,154,33,0.3)", color: "#d39a21", fontSize: 8, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase" }}>
              ▶ Video
            </div>
          </div>
        )}

        {/* Gallery nav */}
        {totalMedia > 1 && (
          <div style={{ position: "absolute", inset: 0, display: "flex", justifyContent: "space-between", alignItems: "center", padding: "0 8px", pointerEvents: "none" }}>
            <button
              onClick={prevImage}
              style={{
                pointerEvents: "auto",
                background: "rgba(0,0,0,0.5)",
                border: "none",
                color: "#fff",
                width: 22,
                height: 22,
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                fontSize: 10
              }}
            >
              ⟨
            </button>
            <button
              onClick={nextImage}
              style={{
                pointerEvents: "auto",
                background: "rgba(0,0,0,0.5)",
                border: "none",
                color: "#fff",
                width: 22,
                height: 22,
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                fontSize: 10
              }}
            >
              ⟩
            </button>
          </div>
        )}

        {/* Dot indicators */}
        {totalMedia > 1 && (
          <div style={{ position: "absolute", bottom: 6, left: 0, right: 0, display: "flex", justifyContent: "center", gap: 4, zIndex: 2, pointerEvents: "none" }}>
            {allMedia.map((m, i) => (
              <div key={i} style={{
                width:        currentImgIndex === i ? (m.type === "video" ? 14 : 10) : 4,
                height:       2,
                borderRadius: 1,
                background:   currentImgIndex === i ? "#d39a21" : "rgba(255,255,255,0.4)",
                transition:   "all 0.3s",
              }} />
            ))}
          </div>
        )}

        {/* Gradient */}
        <div
          aria-hidden="true"
          style={{
            position:   "absolute",
            inset:      0,
            background: "linear-gradient(180deg, transparent 38%, rgba(0,0,0,0.55) 100%)",
            pointerEvents: "none"
          }}
        />

        {/* Hover overlay, shown when not active, prompts Aura chat */}
        {!isActive && hov && (
          <div
            aria-hidden="true"
            style={{
              position:       "absolute",
              inset:          0,
              background:     "rgba(0,0,0,0.38)",
              display:        "flex",
              alignItems:     "center",
              justifyContent: "center",
              gap:            6,
              pointerEvents:  "none",
            }}
          >
            <Icon name="sparkleMini" size={13} color={GOLD} />
            <span style={{
              fontFamily:    "var(--font-body)",
              fontSize:      10.5,
              fontWeight:    600,
              color:         "#fff",
              letterSpacing: "0.2px",
            }}>
              Ask Aura about this venue
            </span>
          </div>
        )}

        {/* Online dot, top-left */}
        <div
          style={{
            position:   "absolute",
            top:        8,
            left:       8,
            display:    "flex",
            alignItems: "center",
            gap:        4,
            background: "rgba(0,0,0,0.45)",
            borderRadius: 20,
            padding:    "2px 7px 2px 5px",
          }}
        >
          <div
            aria-hidden="true"
            style={{
              width:        5,
              height:       5,
              borderRadius: "50%",
              background:   item.online ? "#22c55e" : "#6b7280",
              flexShrink:   0,
            }}
          />
          <span
            style={{
              fontFamily: "var(--font-body)",
              fontSize:   8,
              color:      item.online ? "#22c55e" : "rgba(255,255,255,0.3)",
            }}
          >
            {item.online ? "Online" : "Offline"}
          </span>
        </div>

        {/* Tag or category badge */}
        {(item.tag || isVendor) && (
          <div
            style={{
              position:      "absolute",
              bottom:        8,
              left:          8,
              padding:       "3px 8px",
              borderRadius:  "var(--lwd-radius-input)",
              background:    "rgba(211,154,33,0.92)",
              fontSize:      9,
              fontWeight:    700,
              letterSpacing: "1px",
              textTransform: "uppercase",
              color:         "#0f0d0a",
            }}
          >
            {item.tag ?? item.categoryLabel ?? item.category}
          </div>
        )}

        {/* Save */}
        <button
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            if (!saved) {
              if (queryId && sessionId) trackAuraClick(queryId, sessionId, item.id, item.rank ?? 0, 'shortlist');
              // Sync into Aura shortlist (powers compare), only if space remains
              if (!shortlistFull) auraShortlistVenue(item);
            } else {
              // Remove from Aura shortlist when un-hearting
              auraShortlistRemove(item.id);
            }
            toggleItem(item);
          }}
          aria-label={saved ? `Remove ${item.name} from shortlist` : `Save ${item.name}`}
          aria-pressed={saved}
          style={{
            position:       "absolute",
            top:            8,
            right:          8,
            width:          28,
            height:         28,
            borderRadius:   "50%",
            background:     saved ? "rgba(211,154,33,0.92)" : "rgba(0,0,0,0.45)",
            border:         "none",
            color:          saved ? "#0f0d0a" : "rgba(255,255,255,0.8)",
            cursor:         "pointer",
            fontSize:       13,
            display:        "flex",
            alignItems:     "center",
            justifyContent: "center",
            zIndex:         2
          }}
        >
          <Icon name={saved ? "heartFilled" : "heart"} size={13} />
        </button>
      </div>

      {/* ── Content ── */}
      <div style={{ padding: "11px 13px 13px" }}>
        {/* Name + verified */}
        <div
          style={{
            display:        "flex",
            alignItems:     "flex-start",
            justifyContent: "space-between",
            marginBottom:   2,
          }}
        >
          <a
            href={listingUrl}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              fontFamily: "var(--font-heading-primary)",
              fontSize:   16,
              fontWeight: 500,
              color:      T.nameColor,
              lineHeight: 1.2,
              flex:       1,
              textDecoration: "none"
            }}
          >
            {item.name}
          </a>
          {item.verified && (
            <span
              aria-label="Verified"
              style={{
                display:      "flex",
                alignItems:   "center",
                fontSize:     8,
                color:        "#22c55e",
                border:       "1px solid rgba(34,197,94,0.25)",
                padding:      "2px 5px",
                borderRadius: "var(--lwd-radius-input)",
                marginLeft:   6,
                flexShrink:   0,
                whiteSpace:   "nowrap",
                alignSelf:    "flex-start",
              }}
            >
              <Icon name="checkCircle" size={9} color="#22c55e" />
            </span>
          )}
        </div>

        {/* Location */}
        <div
          style={{
            fontFamily:   "var(--font-body)",
            fontSize:     10,
            color:        T.locColor,
            marginBottom: item.reason ? 4 : 7,
          }}
        >
          {item.city}, {item.region}
        </div>

        {/* Aura info card, reason + match chips, always visible when present.
            Capability chips (confirmed wedding intelligence) lead; generic
            score chips are a fallback only when no capabilities matched. */}
        {(() => {
          const capChips = deriveCapabilityChips(item.matchReasons);
          if (!item.reason && capChips.length === 0) return null;

          let chips = capChips;
          let isCapability = capChips.length > 0;
          if (chips.length === 0 && item.scores) {
            const s = [];
            if ((item.scores.capacityFit          ?? 0) >= 85) s.push("Capacity match");
            if ((item.scores.semanticMatch         ?? 0) >= 70) s.push("Style match");
            if ((item.scores.editorialQuality      ?? 0) >= 60) s.push("Editorial pick");
            if ((item.scores.luxuryProfile         ?? 0) >= 80) s.push("Verified luxury");
            if ((item.scores.behavioralPerformance ?? 0) >= 40) s.push("Popular");
            chips = s;
          }

          return (
            <div
              style={{
                borderLeft:   `2px solid ${GOLD}`,
                paddingLeft:  8,
                marginBottom: 7,
              }}
            >
              {item.reason && (
                <div
                  style={{
                    fontFamily:   "var(--font-body)",
                    fontSize:     11,
                    color:        GOLD,
                    opacity:      0.9,
                    lineHeight:   1.5,
                    marginBottom: chips.length > 0 ? 5 : 0,
                  }}
                >
                  {cleanDashes(item.reason)}
                </div>
              )}
              {chips.length > 0 && (
                <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                  {chips.slice(0, 3).map(chipLabel => (
                    <span
                      key={chipLabel}
                      style={{
                        fontFamily:    "var(--font-body)",
                        fontSize:      8,
                        fontWeight:    600,
                        letterSpacing: "0.8px",
                        textTransform: "uppercase",
                        color:         GOLD,
                        border:        `1px solid ${GOLD}44`,
                        padding:       "2px 6px",
                        borderRadius:  "var(--lwd-radius-input)",
                        whiteSpace:    "nowrap",
                        display:       "inline-flex",
                        alignItems:    "center",
                        gap:           3,
                      }}
                    >
                      {isCapability && <span aria-hidden="true">✓</span>}
                      {chipLabel}
                    </span>
                  ))}
                </div>
              )}
            </div>
          );
        })()}

        {/* LWD Index score + In Focus badge + Contacted indicator (same row) */}
        {(item.lwdScore || isActive || isEnquired) && (
          <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 5 }}>
            {item.lwdScore && <CuratedIndexBadge score={item.lwdScore} label="Aura score" />}
            {isActive && (
              <span style={{
                fontFamily:    "var(--font-body)",
                fontSize:      8,
                fontWeight:    700,
                letterSpacing: "1.1px",
                textTransform: "uppercase",
                color:         "#818cf8",
                border:        "1px solid rgba(129,140,248,0.32)",
                padding:       "2px 7px",
                borderRadius:  "var(--lwd-radius-input)",
                whiteSpace:    "nowrap",
                lineHeight:    1.5,
              }}>
                In Focus
              </span>
            )}
            {isEnquired && (
              <span style={{
                fontFamily:    "var(--font-body)",
                fontSize:      8,
                fontWeight:    600,
                letterSpacing: "0.8px",
                textTransform: "uppercase",
                color:         "#10b981",
                border:        "1px solid rgba(16,185,129,0.3)",
                padding:       "2px 7px",
                borderRadius:  "var(--lwd-radius-input)",
                whiteSpace:    "nowrap",
                lineHeight:    1.5,
              }}>
                ✓ Contacted
              </span>
            )}
          </div>
        )}

        {item.rating && (
          <div
            style={{ display: "flex", alignItems: "center", gap: 4, marginBottom: 7 }}
          >
            <StarRating rating={item.rating} size={10} />
            <span
              style={{
                fontFamily: "var(--font-body)",
                fontSize:   10,
                color:      T.ratingColor,
              }}
            >
              {item.rating} ({item.reviews})
            </span>
          </div>
        )}

        {/* Features */}
        {features.length > 0 && (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginBottom: 10 }}>
            {features.slice(0, 3).map((f) => (
              <span
                key={f}
                style={{
                  fontFamily:    "var(--font-body)",
                  fontSize:      9,
                  color:         T.tagColor,
                  border:        `1px solid ${T.tagBorder}`,
                  borderRadius:  "var(--lwd-radius-input)",
                  padding:       "2px 6px",
                  letterSpacing: "0.2px",
                }}
              >
                {f}
              </span>
            ))}
          </div>
        )}

        {/* Footer: price + action buttons */}
        <div
          style={{
            display:        "flex",
            justifyContent: "space-between",
            alignItems:     "center",
            paddingTop:     8,
            borderTop:      `1px solid ${T.divider}`,
          }}
        >
          <div>
            <div
              style={{
                fontFamily:    "var(--font-heading-primary)",
                fontSize:      18,
                color:         GOLD,
                fontWeight:    600,
                letterSpacing: "0.3px",
                lineHeight:    1,
              }}
            >
              {item.priceFrom}
            </div>
            {item.capacity && (
              <div
                style={{
                  fontFamily:    "var(--font-body)",
                  fontSize:      9,
                  color:         T.capColor,
                  marginTop:     3,
                  letterSpacing: "0.3px",
                }}
              >
                Up to {item.capacity} guests
              </div>
            )}
          </div>

          {/* Buttons: Chat + Preview + View */}
          <div style={{ display: "flex", gap: 4, flexWrap: "wrap", fontSize: 8 }}>
            {/* Chat, always active, focuses venue + sends Aura message */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                setActiveVenueId(item.id);
                auraShortlistVenue(item);
                sendMessage(`Tell me more about ${item.name}`);
              }}
              title={`Ask Aura about ${item.name}`}
              style={{
                fontFamily:    "var(--font-body)",
                fontSize:      9,
                fontWeight:    700,
                letterSpacing: "0.8px",
                textTransform: "uppercase",
                color:         "#0D0B09",
                background:    "rgba(211,154,33,0.82)",
                border:        "none",
                borderRadius:  "var(--lwd-radius-input)",
                padding:       "5px 8px",
                cursor:        "pointer",
                transition:    "opacity 0.2s",
                display:       "flex",
                alignItems:    "center",
                gap:           3,
                minWidth:      "fit-content",
              }}
              onMouseEnter={(e) => { e.currentTarget.style.opacity = "0.82"; }}
              onMouseLeave={(e) => { e.currentTarget.style.opacity = "1"; }}
            >
              Chat
            </button>

            {/* Quick View Modal */}
            <button
              onClick={handleQuickView}
              style={{
                fontFamily:    "var(--font-body)",
                fontSize:      9,
                fontWeight:    600,
                letterSpacing: "0.8px",
                textTransform: "uppercase",
                color:         GOLD,
                background:    "rgba(211,154,33,0.12)",
                border:        `1px solid ${T.qvBorder}`,
                borderRadius:  "var(--lwd-radius-input)",
                padding:       "5px 8px",
                cursor:        "pointer",
                transition:    "all 0.2s",
                display:       "flex",
                alignItems:    "center",
                gap:           3,
              }}
              title="Quick preview with multiple images"
            >
              Preview
            </button>

            {/* View Profile - Open in new tab */}
            <a
              href={listingUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => { e.stopPropagation(); if (queryId && sessionId) trackAuraClick(queryId, sessionId, item.id, item.rank ?? 0, 'profile'); }}
              style={{
                fontFamily:    "var(--font-body)",
                fontSize:      9,
                fontWeight:    600,
                letterSpacing: "0.8px",
                textTransform: "uppercase",
                color:         GOLD,
                background:    "rgba(211,154,33,0.09)",
                border:        `1px solid ${T.qvBorder}`,
                borderRadius:  "var(--lwd-radius-input)",
                padding:       "5px 8px",
                cursor:        "pointer",
                transition:    "all 0.2s",
                display:       "flex",
                alignItems:    "center",
                gap:           3,
                textDecoration: "none"
              }}
              title="Open full listing profile in a new tab"
            >
              View
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
